'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string; // ISO 8601
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const CONSENT_KEY = 'instantloanmitra_consent_v1';

/**
 * Detect user region based on browser locale and timezone
 * No external API calls — uses navigator.language and Intl API
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr'; // SSR default

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  const isUS =
    tz.startsWith('America/') &&
    (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

/**
 * Save consent choices to localStorage
 */
export function saveConsent(choices: ConsentRecord['choices'], region: ConsentRegion): void {
  if (typeof window === 'undefined') return;

  const record: ConsentRecord = {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices,
    userAgent: navigator.userAgent,
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (e) {
    console.warn('Failed to save consent:', e);
  }
}

/**
 * Retrieve consent choices from localStorage
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn('Failed to retrieve consent:', e);
    return null;
  }
}

/**
 * Check if a specific consent category is enabled
 */
export function isConsentGiven(category: keyof ConsentRecord['choices']): boolean {
  const consent = getConsent();
  if (!consent) return false;
  return consent.choices[category] === true;
}

/**
 * Clear consent record from localStorage
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (e) {
    console.warn('Failed to clear consent:', e);
  }
}

/**
 * Log consent action for audit trail
 */
export function logConsentAction(action: string, details?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const logEntry = {
    timestamp: new Date().toISOString(),
    action,
    region: detectRegion(),
    details,
  };

  console.log('[Consent]', logEntry);
}
